import GoogleMapReact, { MapOptions } from 'google-map-react';
import React from 'react';
import { FaMapMarkerAlt } from 'src/shared/design-system/FontAwesome';
import { minScreen } from 'src/shared/utils/StyledComponentUtils';
import styled, { css } from 'styled-components';
import { Colors, Font, ScreenWidth } from 'web-common/src/shared/styles';

interface GoogleMapProps {
  className?: string;
  defaultZoom?: number;
  latitude: number;
  longitude: number;
  children?: JSX.Element;
  options?: MapOptions;
}

const GoogleMapContainer = styled.div`
  border-radius: 8px;
  height: 180px;
  overflow: hidden;

  ${minScreen(ScreenWidth.MEDIUM)} {
    border-radius: 14px;
    height: 210px;
  }
`;

export const GoogleMap: React.FC<GoogleMapProps> = ({
  className,
  defaultZoom = 16,
  latitude,
  longitude,
  children,
  options = {},
}) => {
  const optionsWithDefaults = {
    minZoom: 8,
    maxZoom: 16,
    ...options,
  };
  const coords = {
    lat: latitude,
    lng: longitude,
  };

  return (
    <GoogleMapContainer className={className}>
      <GoogleMapReact center={coords} defaultZoom={defaultZoom} options={optionsWithDefaults}>
        {/* Ensures that marker is on the right coords. Used by GoogleMapReact. */}
        {children && React.cloneElement(children, coords)}
      </GoogleMapReact>
    </GoogleMapContainer>
  );
};

interface GoogleMapMarkerProps {
  className?: string;
  children: JSX.Element;
}

/**
 * General purpose map marker component that will center the marker on the map.
 */
export const GoogleMapMarker: React.FC<GoogleMapMarkerProps> = ({ className, children }) =>
  React.cloneElement(children, {
    className,
    style: {
      ...children.props.style,
      // Initial positioning for map objects is top-left corner of lat/lng. This moves it to the center.
      // https://github.com/google-map-react/google-map-react/blob/master/API.md#positioning-a-marker
      position: 'absolute',
      transform: 'translate(-50%, -50%)',
    },
  });

const StyledFaMapMarkerAlt = styled(FaMapMarkerAlt)`
  margin-top: -10px;
  font-size: ${Font.Size.XL};
  color: ${Colors.RADISH};
`;

export const GoogleMapPinMarker: React.FC<Pick<GoogleMapMarkerProps, 'className'>> = (props) => (
  <GoogleMapMarker {...props}>
    <StyledFaMapMarkerAlt />
  </GoogleMapMarker>
);

const AreaMarker = styled.div<{ radius: number }>`
  background-color: rgba(218, 58, 74, 0.3);
  border-radius: 50%;
  border: 1px solid ${Colors.RADISH};
  opacity: 0.3;

  ${({ radius }) => css`
    width: ${radius}px;
    height: ${radius}px;
  `}
`;

interface GoogleMapAreaMarkerProps {
  className?: string;
  radius?: number;
}

export const GoogleMapAreaMarker: React.FC<GoogleMapAreaMarkerProps> = ({ className, radius = 107 }) => (
  <GoogleMapMarker className={className}>
    <AreaMarker radius={radius} />
  </GoogleMapMarker>
);
